/* @flow */

import React, { Component } from 'react';
import { hover } from 'app/utils/cssUtils';
import { clamp } from 'lodash/fp';
import classNames from 'classnames';
import Swipeable from 'react-swipeable';
import Tappable from 'app/components/commons/Tappable';
import useSheet from '../../jss';

type Props = {
  children: Array<any>,
  initialPosition?: number,
  sheet: Object,
  slidesToScroll?: number,
  slidesToShow?: number,
};

type State = {
  pos: number,
};

class Carousel extends Component<Props, State> {
  props: Props;
  
  constructor(props: Props) {
    super(props);
    const children = props.children || [];
    const childrenLength = Array.isArray(children) ? children.length : (children ? 1 : 0);
    this.state = {
      pos: clamp(0, childrenLength - props.slidesToShow, props.initialPosition),
    };
  }

  static defaultProps = {
    initialPosition: 0,
    slidesToScroll: 1,
    slidesToShow: 1,
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setPosition(this.state.pos, nextProps.slidesToShow, nextProps.children);
  }

  setPosition(pos, slidesToShow = this.props.slidesToShow, children = this.props.children) {
    const childrenArray = children || [];
    const childrenLength = Array.isArray(childrenArray) ? childrenArray.length : (childrenArray ? 1 : 0);
    const finalPos = childrenLength - slidesToShow;

    this.setState({
      pos: clamp(0, finalPos, pos),
    });
  }

  prev = () => {
    const { slidesToScroll } = this.props;
    this.setPosition(this.state.pos - slidesToScroll);
  };

  next = () => {
    const { slidesToScroll } = this.props;
    this.setPosition(this.state.pos + slidesToScroll);
  };

  render() {
    const { children, sheet: { classes }, slidesToShow } = this.props;
    const { pos } = this.state;

    const childrenArray = children || [];
    const childrenLength = Array.isArray(childrenArray) ? childrenArray.length : (childrenArray ? 1 : 0);
    const showArrows = slidesToShow < childrenLength;
    const canSlideLeft = pos > 0;
    const canSlideRight = pos + slidesToShow < childrenLength;
    const slideWidth = 100 / slidesToShow;
    const sliderOffset = slideWidth * pos;

    return (
      <div className={classes.wrapper}>
        <Swipeable className={classes.container} onSwipedRight={this.prev} onSwipedLeft={this.next}>
          <div
            className={classes.scrollableContainer}
            style={{
              justifyContent: showArrows ? 'flex-start' : 'center',
              transform: `translateX(-${sliderOffset}%)`,
            }}
          >
            {(Array.isArray(childrenArray) ? childrenArray : [childrenArray]).map((child, i) => (
              <div
                key={i} // eslint-disable-line react/no-array-index-key
                className={classes.item}
                style={{ flexBasis: `${slideWidth}%` }}
              >
                <div className={classes.itemInner}>{child}</div>
              </div>
            ))}
          </div>
        </Swipeable>
        {showArrows && (
          <div className={classes.sliderArrows}>
            <Tappable
              onClick={this.prev}
              className={classNames(classes.sliderArrow, classes.sliderArrow__back, {
                [classes.sliderArrow__inactive]: !canSlideLeft,
              })}
            >
              <span className={classes.sliderArrow__icon}>
                <span className="ion-ios-arrow-back" />
              </span>
            </Tappable>
            <Tappable
              onClick={this.next}
              className={classNames(classes.sliderArrow, classes.sliderArrow__forward, {
                [classes.sliderArrow__inactive]: !canSlideRight,
              })}
            >
              <span className={classes.sliderArrow__icon}>
                <span className="ion-ios-arrow-forward" />
              </span>
            </Tappable>
          </div>
        )}
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    wrapper: {
      position: 'relative',
    },
    container: {
      marginLeft: 25,
      marginRight: 25,
      overflow: 'hidden',
      position: 'relative',
    },
    item: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: '100%',
      minWidth: 0,
    },
    itemInner: {
      display: 'flex',
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 'auto',
      justifyContent: 'center',
      maxWidth: '100%',
      padding: theme.spacing.xsmall,
    },
    scrollableContainer: {
      display: 'flex',
      overflow: 'visible',
      position: 'relative',
      transition: 'transform 0.6s cubic-bezier(.15, .9, .25, 1)',
      zIndex: 1,
    },
    sliderArrow: {
      alignItems: 'center',
      bottom: 0,
      color: theme.color.basis3,
      opacity: 0.8,
      display: 'flex',
      justifyContent: 'center',
      position: 'absolute',
      transition: 'color 0.2s',
      top: 0,
      width: theme.spacing.default * 2,
      ...hover({
        opacity: 1,
      }),
    },
    sliderArrow__back: {
      left: -10,
    },
    sliderArrow__forward: {
      right: -10,
    },
    sliderArrow__icon: {
      fontSize: theme.fontSize.large,
    },
    sliderArrow__inactive: {
      cursor: 'default',
      '&, &:hover, &:active, &:focus': {
        opacity: 0.15,
      },
    },
  };
}

export default useSheet(applyStyles)(Carousel);
